import React, { createContext, useContext, useEffect, useState } from 'react';
import { Amplify, Auth } from 'aws-amplify';
import axios from 'axios';
import jwt from 'jsonwebtoken';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

import { useSnackbar } from 'contexts/snackbar';
import accountRequests from 'services/account';
import adminRequests from 'services/admin';
import authRequests from 'services/auth';
import courseRequests from 'services/course';
import userRequests from 'services/user';

// manual setup
Amplify.configure({
    Auth: {
        // REQUIRED only for Federated Authentication - Amazon Cognito Identity Pool ID
        // identityPoolId: 'XX-XXXX-X:XXXXXXXX-XXXX-1234-abcd-1234567890ab',

        // REQUIRED - Amazon Cognito Region
        region: process.env.REACT_APP_AWS_REGION,

        // OPTIONAL - Amazon Cognito Federated Identity Pool Region
        // Required only if it's different from Amazon Cognito Region
        // identityPoolRegion: 'XX-XXXX-X',

        // OPTIONAL - Amazon Cognito User Pool ID
        userPoolId: process.env.REACT_APP_USERPOOL_ID,

        // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
        userPoolWebClientId: process.env.REACT_APP_CLIENT_ID,

        // OPTIONAL - Enforce user authentication prior to accessing AWS resources or not
        mandatorySignIn: false,
    },
});

const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

const AuthProvider = (props) => {
    const history = useHistory();
    const location = useLocation();
    const { message } = useSnackbar();

    const [auth, setAuth] = useState();
    const [currentUser, setCurrentUser] = useState();
    const [loading, setLoading] = useState(false);

    const isLTI = false;

    const updateCurrentUser = async (data) => {
        const updateUser = await userRequests.updateUser(auth.user_id, data);
        setCurrentUser(updateUser);
        return updateUser;
    };

    const login = async ({ username = '', password = '' }) => {
        try {
            await Auth.signOut();
            const trimmedUsername = username?.toLowerCase()?.trim();
            const signInCognito = await Auth.signIn(trimmedUsername, password);
            if (signInCognito.challengeName === 'NEW_PASSWORD_REQUIRED') {
                await Auth.completeNewPassword(signInCognito, password);
                await Auth.signIn(trimmedUsername, password);
            }
            if (!signInCognito) throw new Error();
            const res = await authRequests.signin({
                username: trimmedUsername,
                cognito_user_id: signInCognito.username,
            });
            const { auth_token: token } = res;
            if (!res) return new Error();
            localStorage.setItem('auth_token', token);
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
            const cachedUser = jwt.decode(token);
            if (cachedUser.owner_account_id) {
                const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
                setAuth({ ...cachedUser, ...accountData });
            } else {
                setAuth({ ...cachedUser });
            }
            setCurrentUser(res);

            if (res.admin === 'true') return 'admin';
            else if (!res?.profile_set_up) {
                return 'profile-set-up';
            } else {
                return 'login';
            }
        } catch (e) {
            console.log(e);
            throw new Error();
        }
    };

    const sessionExpired = () => {
        return logout({ isExpired: true });
    };

    const logout = async ({ isExpired = false }) => {
        localStorage.removeItem('auth_token');
        axios.defaults.headers.common.Authorization = undefined;
        await Auth.signOut();
        setAuth(undefined);
        if (isExpired) {
            setLoading(false);
            return history.push('/', { isExpired });
        }
        return history.push('/');
    };

    const selectAccount = async (accountId) => {
        setLoading(true);
        const res = await authRequests.selectAccount({ owner_account_id: accountId });
        const { auth_token: token } = res;
        localStorage.setItem('auth_token', token);
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        const cachedUser = jwt.decode(token);
        const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
        const courseData = await courseRequests.findCourse(accountData.course_id);
        const groupFormation = await courseRequests.listCourseGroupFormations(accountData?.course_id);
        const survey = await courseRequests.listCourseGroupSurveys(accountData?.course_id, {
            owner_account_id: accountData?.id,
        });
        setAuth({ ...cachedUser, ...accountData });
        if (
            !courseData?.finished_course_set_up &&
            accountData?.role !== 'student' &&
            courseData?.created_on > new Date('2021-12-01').getTime()
        ) {
            history.push('/course-set-up');
        } else if (
            accountData.role === 'student' &&
            groupFormation &&
            !courseData.finished_group_generation &&
            !accountData.group_id
        ) {
            history.push('/survey');
        } else if (accountData?.role === 'instructor') {
            history.push('/overview');
        } else if (!accountData?.group_id) {
            history.push('/lti-incomplete');
        } else {
            history.push('/dashboard');
        }
        setLoading(false);
    };

    const startCourseSetup = async () => {
        setLoading(true);
        const course = await adminRequests.createCourse({
            school: '',
            name: '',
            faculty: '',
            semester: '',
            section: '',
            finished_course_set_up: false,
        });
        const [{ account }] = await adminRequests.createInstructors({
            course_id: course.id,
            instructors: [{ username: currentUser.username }],
        });

        const res = await authRequests.selectAccount({ owner_account_id: account.id });
        const { auth_token: token } = res;
        localStorage.setItem('auth_token', token);
        const cachedUser = jwt.decode(token);
        const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
        setAuth({ ...cachedUser, ...accountData });

        history.push('/course-set-up');
        setLoading(false);
    };

    const verifyUser = async (username, code) => {
        try {
            await Auth.confirmSignUp(username, code);
            return true;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e.toString());
            const findConfirmed = e.toString().includes('CONFIRMED');
            if (findConfirmed) {
                return true;
            }
            message.error('Something went wrong. Please try again later.');
            return await logout({ isExpired: false });
        }
    };

    const resendConfirmationCode = async (username) => {
        try {
            await Auth.resendSignUp(username);
            return message.success('The email resent successfully');
        } catch (err) {
            console.log('error resending code: ', err);
        }
    };

    const updatePassword = async (oldPassword, newPassword) => {
        return Auth.currentAuthenticatedUser()
            .then(async (user) => {
                if (!newPassword) return new Error();
                await Auth.changePassword(user, oldPassword || currentUser.password, newPassword);
                return true;
            })
            .catch((e) => {
                console.log(e);
                return false;
            });
    };

    const register = async ({ username, password }) => {
        try {
            const trimmedUsername = username?.toLowerCase()?.trim();
            const signUpData = {
                display_name: trimmedUsername,
                name: trimmedUsername,
                first_name: trimmedUsername,
                username: trimmedUsername,
                email: trimmedUsername,
                password,
                admin: false,
                base_url: window.location.origin,
            };
            const createUser = await authRequests.signUp(signUpData);
            return createUser;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
            return e.data;
        }
    };

    const forgotPassword = async (email) => {
        try {
            console.log(email);
            const findUser = await authRequests.forgotPassword({ username: email });
            return findUser;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    };

    const confirmForgotPassword = async (email, code, newPassword) => {
        try {
            const forgotPasswordConfirmReq = await Auth.forgotPasswordSubmit(
                email?.toLowerCase()?.trim(),
                code,
                newPassword
            );
            return forgotPasswordConfirmReq;
        } catch (e) {
            console.log(e);
            return false;
        }
    };

    const getCurrentUser = () => currentUser || null;

    const updateNotificationPreferences = async (newNotification) => {
        try {
            setAuth((auth) => ({
                ...auth,
                notification: newNotification,
            }));
            await accountRequests.updateAccount(auth.owner_account_id, { notification: newNotification });
        } catch (e) {
            console.error(e);
            message.error('Could not update notification preferences.');
        }
    };

    const updateProfile = async (data) => {
        const prevUser = { ...currentUser };
        const updatedUser = {
            ...currentUser,
            ...data,
        };
        setCurrentUser(updatedUser);

        try {
            await userRequests.updateUser(currentUser.id, data);
            message.success('Profile successfully updated.');
        } catch (e) {
            setCurrentUser(prevUser);
            message.error('Could not update profile.');
        }
    };

    const changeAccount = () => {
        setAuth((current) => ({ iat: current.iat, user_id: current.user_id, username: current.username }));
        return history.push('/');
    };

    useEffect(() => {
        const getCurrentSession = async () => {
            let cachedUser = null;

            let token = localStorage.getItem('auth_token');
            if (auth && location.pathname === '/lti-launch') {
                const { token: ltiLaunchToken } = queryString.parse(location.search);
                localStorage.removeItem('auth_token');
                if (ltiLaunchToken) {
                    localStorage.setItem('auth_token', ltiLaunchToken);
                    token = ltiLaunchToken;
                }
            }
            if (location.pathname === '/accepted-invitation') {
                setLoading(true);
                const { token: incomingToken, confirmation_code } = queryString.parse(location.search);
                localStorage.setItem('auth_token', incomingToken);
                cachedUser = jwt.decode(incomingToken);
                axios.defaults.headers.common.Authorization = `Bearer ${incomingToken}`;
                try {
                    const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
                    const userData = await userRequests.findUser(cachedUser.user_id);
                    if (userData?.finished_sign_up || userData?.profile_set_up) {
                        message.success('Thank you for accepting the invitation. Please login again.');
                        setLoading(false);
                        return logout({ isExpired: false });
                    }
                    setAuth({ ...cachedUser, ...accountData });
                    setCurrentUser(userData);
                    if (confirmation_code) {
                        const verifyingConfirmationCode = await verifyUser(userData.username, confirmation_code);
                        if (!verifyingConfirmationCode) return setLoading(false);
                    }

                    await login({ username: userData.username, password: userData.password });
                    setLoading(false);
                } catch (e) {
                    setLoading(false);
                    console.log(e);
                    message.error(
                        'The invitation link is expired. Please ask your instructor to get a new invitation link.'
                    );
                    return logout({ isExpired: false });
                }
            } else if (token) {
                setLoading(true);
                cachedUser = jwt.decode(token);
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;
                if (cachedUser?.owner_account_id) {
                    const accountData = await accountRequests.findAccount(cachedUser.owner_account_id);
                    if (accountData?.active === false) return await logout({ isExpired: true });
                    setAuth({ ...cachedUser, ...accountData });
                    const userData = await userRequests.findUser(cachedUser.user_id);
                    setCurrentUser(userData);
                    if (accountData?.course_id) {
                        const existingCourseData = await courseRequests.findCourse(accountData?.course_id);
                        const groupFormation = await courseRequests.listCourseGroupFormations(accountData?.course_id);

                        if (!cachedUser.group_id && cachedUser.role === 'student' && !groupFormation) {
                            history.push('/lti-incomplete');
                        }

                        if (
                            cachedUser.role === 'instructor' &&
                            !existingCourseData?.finished_course_set_up &&
                            existingCourseData?.created_on > new Date('2021-12-01').getTime()
                        ) {
                            history.push('/course-set-up');
                        }
                        if (
                            cachedUser.role === 'instructor' &&
                            !existingCourseData.finished_course_set_up &&
                            groupFormation &&
                            !existingCourseData.finished_group_generation
                        ) {
                            history.push('/course-set-up');
                        }

                        if (
                            cachedUser.role === 'student' &&
                            groupFormation &&
                            !existingCourseData.finished_group_generation &&
                            !accountData.group_id
                        ) {
                            history.push('/survey');
                        }

                        if (userData && cachedUser?.role && cachedUser.role !== 'admin' && !userData.profile_set_up) {
                            history.push('/profile-setup');
                        }
                    }
                } else {
                    const userData = await userRequests.findUser(cachedUser.user_id);
                    setCurrentUser(userData);
                    setAuth({ ...cachedUser, ...userData });
                    if (userData && !userData.profile_set_up) {
                        history.push('/profile-setup');
                    }
                }
                try {
                    await Auth.currentSession();
                    setLoading(false);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error(e);
                    return logout({ isExpired: true });
                }
            }
        };

        if (!auth) {
            getCurrentSession();
        }
    }, [auth, location.pathname, location.search]);

    return (
        <AuthContext.Provider
            value={{
                loading,
                auth,
                currentUser,
                setAuth,
                isLTI,
                getCurrentUser,
                updateCurrentUser,
                updateNotificationPreferences,
                updateProfile,
                login,
                logout,
                forgotPassword,
                updatePassword,
                selectAccount,
                changeAccount,
                confirmForgotPassword,
                resendConfirmationCode,
                register,
                verifyUser,
                getCurrentUser,
                sessionExpired,
                setAuth,
                startCourseSetup,
            }}
            {...props}
        />
    );
};

export { useAuth };
export default AuthProvider;
